import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";

import { useIsDemoExperience } from "@av/apps/common-apps/platform/services/demo-experience/demo";
import type { MockApplicantData } from "@av/apps/recruitment/ats-common/mock-db/candidate-pool.types";
import { useMockDbStore } from "@av/apps/recruitment/ats-common/mock-db/mock-db-store";
import { resolveLocation } from "@av/apps/recruitment/ats-common/services/lookup-entities/provinces/provinces";
import { type ApplicationModel } from "@av/apps/recruitment/ats-common/types";
import { bffAxios } from "@av/config/api-config/axios-config";
import { dayjs } from "@av/config/extended-dayjs";

import type {
  ApplicationCreateResponse,
  CandidateDataUI,
  DocumentCreateResponse,
  UploadFileExtended,
  UploadTarget,
} from "./types";
import type { ApiError } from "../types";

function buildDocumentPayload(uploadFile: UploadFileExtended): UploadTarget {
  const size = typeof uploadFile.size === "number" ? uploadFile.size : 0;

  if (size < 1) {
    throw new Error("Invalid file: size must be at least 1 byte");
  }

  return {
    uploadFile,
    document: {
      applicationDocumentId: crypto.randomUUID(),
      documentType: uploadFile.documentType,
      creatorType: "CANDIDATE",
      name: uploadFile.name,
      filename: uploadFile.name,
      contentType: uploadFile.type || "application/octet-stream",
      fileSizeBytes: size,
    },
  };
}

async function uploadDocuments(
  uploadTargets: UploadTarget[],
  documentResponses: DocumentCreateResponse[]
) {
  const uploadsById = new Map(
    uploadTargets.map((uploadTarget) => [uploadTarget.document.applicationDocumentId, uploadTarget])
  );
  const responseIds = new Set(
    documentResponses.map((documentResponse) => documentResponse.applicationDocumentId)
  );
  const responseIdsMatch =
    uploadsById.size === uploadTargets.length &&
    responseIds.size === documentResponses.length &&
    responseIds.size === uploadsById.size &&
    [...responseIds].every((documentId) => uploadsById.has(documentId));

  if (!responseIdsMatch) {
    throw new Error("Upload response did not match submitted documents");
  }

  await Promise.all(
    documentResponses.map((document) => {
      const uploadTarget = uploadsById.get(document.applicationDocumentId);

      if (!uploadTarget) {
        throw new Error("Upload response did not match submitted documents");
      }

      const formData = new FormData();

      Object.entries(document.presignedPostFields).forEach(([key, value]) =>
        formData.append(key, value)
      );

      formData.append("file", uploadTarget.uploadFile.originFileObj as Blob);

      return axios.post(document.presignedPostUrl, formData);
    })
  );
}

export function usePublicApplyToJob() {
  const addExternalCandidateToJob = useMockDbStore((s) => s.addExternalCandidateToJob);
  const isDemoExperience = useIsDemoExperience();
  const isCoverLetterEnabled = useBooleanFlagValue("ats_multi_docs", false);
  const areCandidateSupportingDocumentsEnabled = useBooleanFlagValue(
    "ats_candidate_supporting_document_uploads",
    false
  );

  return useMutation<void, ApiError, CandidateDataUI>({
    retry: false,
    mutationFn: async (variables: CandidateDataUI) => {
      const { candidate, screeningAnswers } = variables;

      if (isDemoExperience) {
        // Demo mode skips ATS validation, so drop a whitespace-only middle name here instead.
        const middleName = candidate.middleName?.trim() || undefined;
        const payload: Partial<MockApplicantData> = {
          firstName: candidate.firstName,
          middleName,
          lastName: candidate.lastName,
          name: `${candidate.firstName} ${candidate.lastName}`.trim(),
          workEntitlement: variables.workEntitlement,
          fitReason: variables.fitReason,
          yearsWorked: variables.yearsWorked,
          jobPostId: variables.jobPostId,
          appliedAt: dayjs().toISOString(),
        };

        addExternalCandidateToJob(variables.jobPostId, payload);

        return;
      }

      const uploadTargets: UploadTarget[] = [];

      if (variables.documents.resume) {
        uploadTargets.push(buildDocumentPayload(variables.documents.resume));
      }

      if (isCoverLetterEnabled && variables.documents.coverLetter) {
        uploadTargets.push(buildDocumentPayload(variables.documents.coverLetter));
      }

      if (areCandidateSupportingDocumentsEnabled) {
        variables.documents.supportingDocuments?.forEach((supportingDocument) => {
          uploadTargets.push(buildDocumentPayload(supportingDocument));
        });
      }

      const { country, subdivision } = resolveLocation(candidate.subdivision);
      const applicationId = crypto.randomUUID();
      const jobApplicationPayload: ApplicationModel = {
        applicationId,
        candidate: {
          firstName: candidate.firstName,
          middleName: candidate.middleName || undefined,
          lastName: candidate.lastName,
          email: candidate.email,
          phoneNumber: candidate.phoneNumber,
          linkedIn: candidate.linkedIn,
          country,
          subdivision,
          city: candidate.city,
        },
        documents: uploadTargets.map((uploadTarget) => uploadTarget.document),
        screeningAnswers,
        dataSharingConsentGiven: variables.dataSharingConsentGiven,
      };

      const jobApplicationResponse = await bffAxios.post<{ data: ApplicationCreateResponse }>(
        `/public/job-posts/${variables.jobPostId}/applications`,
        jobApplicationPayload
      );

      await uploadDocuments(uploadTargets, jobApplicationResponse.data.data.documents);
    },
  });
}
